import { NgIf, AsyncPipe } from '@angular/common';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatPrefix, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { RootState } from '@next/core-lib/app';
import { AgrConnectFormDirective, AgrNumberDirective } from '@next/core-lib/form';
import { CurrencySymbolPipe, TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { take, filter, tap } from 'rxjs/operators';

import { Costs, Cycle, ClosureFormData } from '../../models/cycle.model';
import * as cycleActions from '../../store/actions/cycle.actions';
import { getCosts } from '../../store/selectors/costs.selectors';
import { getSelectedCycle } from '../../store/selectors/cycle.selectors';

@Component({
  selector: 'app-closure',
  templateUrl: 'closure.component.html',
  styleUrls: ['closure.component.scss'],
  imports: [
    NgIf,
    ReactiveFormsModule,
    AgrConnectFormDirective,
    MatFormField,
    MatPrefix,
    MatLabel,
    MatInput,
    AgrNumberDirective,
    MatButton,
    AsyncPipe,
    CurrencySymbolPipe,
    TranslatePipe,
    AgrNumberPipe,
  ],
})
export class ClosureComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);

  closureForm = new FormGroup({
    total: new FormControl({ value: null, disabled: true }, []),
    water: new FormControl(null, []),
    heating: new FormControl(null, []),
    electricity: new FormControl(null, []),
    cadaver: new FormControl(null, []),
    certification: new FormControl(null, []),
    healthCare: new FormControl(null, []),
    litter: new FormControl(null, []),
    catching: new FormControl(null, []),
    manure: new FormControl(null, []),
    decontamination: new FormControl(null, []),
    waterAdditives: new FormControl(null, []),
    avined: new FormControl(null, []),
    veterinary: new FormControl(null, []),
    employment: new FormControl(null, []),
    otherCosts: new FormControl(null, []),
    otherRevenue: new FormControl(null, []),
  });

  initialValues: ClosureFormData = this.closureForm.value as ClosureFormData;
  cycle$: Observable<Cycle> = this.store.select(getSelectedCycle);
  location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);

  unspecifiedCosts$: Observable<ClosureFormData> = this.closureForm.valueChanges.pipe(
    tap(({ ...costsData }: ClosureFormData) => {
      this.setTotal(costsData);
    }),
  );

  costs$: Observable<object> = this.store.pipe(
    select(getCosts),
    filter((costs) => !!costs),
    tap(({ ...costsData }: ClosureFormData) => {
      this.setTotal(costsData);
    }),
    take(1),
  );

  private subscription: Subscription;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  private setTotal(costsData: ClosureFormData) {
    const total = this.sumCosts(costsData, true) - costsData.otherRevenue;
    this.closureForm.controls.total.setValue(total, {
      emitEvent: false,
    });
  }

  ngOnInit() {
    this.subscription = this.unspecifiedCosts$.subscribe();
    this.subscription.add(this.costs$.subscribe());
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  sumCosts({ otherRevenue, ...costData }: Costs, filterOtherRevenue = false): number {
    return Object.values(filterOtherRevenue ? costData : { otherRevenue, ...costData })
      .filter((val: number) => val !== null && val !== undefined)
      .map((val: number) => Math.round(val * 100) / 100)
      .reduce((acc: number, value: number) => acc + value, 0);
  }

  save() {
    if (this.closureForm.valid) {
      this.cycle$.pipe(take(1)).subscribe((cycle) => {
        const { total, ...costs }: ClosureFormData = this.closureForm.value as ClosureFormData;
        this.store.dispatch(
          cycleActions.upsertCosts({
            cycleId: cycle.id,
            costs,
          }),
        );
      });
    }
  }
}
